/**
 * Lấy 1 frame video (JPEG) trên trình duyệt — tránh phụ thuộc FFmpeg trên server cho preview/poster.
 * @param {File} file
 * @param {object} [opts]
 * @param {number} [opts.maxWidth=640]
 * @param {number} [opts.quality=0.85]
 * @param {number} [opts.timeoutMs=15000]
 * @returns {Promise<Blob|null>}
 */
export function captureVideoThumbnailFromFile(file, opts = {}) {
  const maxWidth = opts.maxWidth ?? 640;
  const quality = opts.quality ?? 0.85;
  const timeoutMs = opts.timeoutMs ?? 15000;

  if (!file || !file.type || !file.type.startsWith("video/")) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.setAttribute("playsinline", "true");
    video.src = objectUrl;

    let settled = false;
    const finish = (blob) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute("src");
      video.load();
      resolve(blob);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);

    const fail = () => {
      window.clearTimeout(timer);
      finish(null);
    };

    video.onerror = fail;

    video.addEventListener("loadeddata", () => {
      try {
        const d = video.duration;
        const t =
          Number.isFinite(d) && d > 0
            ? Math.min(1, Math.max(0.05, d * 0.1))
            : 0.1;
        video.currentTime = t;
      } catch (e) {
        fail();
      }
    });

    video.addEventListener("seeked", () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) {
          fail();
          return;
        }
        const scale = w > maxWidth ? maxWidth / w : 1;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          fail();
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            window.clearTimeout(timer);
            finish(blob && blob.size > 0 ? blob : null);
          },
          "image/jpeg",
          quality
        );
      } catch (e) {
        fail();
      }
    });
  });
}
